import { useEffect, useMemo, useState, useDeferredValue } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Check, Trash2, Printer, Share2, Loader2, Images, X, FileDown } from "lucide-react";
import { smartMatch } from "../../utils/arabicSearch";
import { printHTML } from "../../utils/printHTML";
import { productImgUrl, buildPhotosPdf, buildPhotosPrintHtml } from "../../utils/productPhotosExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "jumlaty_photo_list_ids";

const ProductPhotosTab = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const dq = useDeferredValue(q);
  const [ids, setIds] = useState(() => { try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch { return []; } });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/products`)
      .then(({ data }) => setProducts(Array.isArray(data) ? data : data.products || []))
      .catch(() => toast.error("فشل تحميل المواد"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify(ids)); }, [ids]);

  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const chosen = ids.map((id) => byId.get(id)).filter(Boolean);
  const list = useMemo(() => {
    const s = dq.trim();
    return s ? products.filter((p) => smartMatch(`${p.name_ar || ""} ${p.barcode || ""} ${p.brand_name || ""}`, s)) : products;
  }, [products, dq]);

  const toggle = (id) => setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const run = async (kind) => {
    if (chosen.length === 0) { toast.error("اختر مادة وحدة على الأقل"); return; }
    setBusy(kind);
    try {
      if (kind === "print") { printHTML(buildPhotosPrintHtml(chosen), { title: "صور المواد" }); return; }
      const blob = await buildPhotosPdf(chosen);
      const file = new File([blob], "صور-المواد-جملتي.pdf", { type: "application/pdf" });
      if (kind === "share" && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "صور المواد - جملتي" });
        return;
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
      if (kind === "share") {
        toast.success("تم تنزيل الملف — أرفقه بالواتساب");
        window.open("https://wa.me/", "_blank");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("صار خطأ، حاول مرة ثانية");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" dir="rtl" data-testid="product-photos-tab">
      <Header count={chosen.length} />
      <Basket chosen={chosen} onRemove={toggle} onClear={() => setIds([])} busy={busy} run={run} />
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن مادة بالاسم أو الباركود..."
          data-testid="photos-search" className="w-full pr-9 pl-3 py-3 rounded-2xl border-2 border-slate-200 focus:border-violet-500 outline-none text-sm bg-white" />
      </div>
      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="photos-grid">
          {list.map((p) => <PickCard key={p.id} p={p} on={ids.includes(p.id)} onClick={() => toggle(p.id)} />)}
        </div>
      )}
    </div>
  );
};

const Header = ({ count }) => (
  <div className="rounded-3xl p-4 text-white shadow-lg flex items-center gap-3" style={{ background: "linear-gradient(135deg,#8B5CF6,#4C1D95)" }}>
    <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center"><Images className="w-7 h-7" /></div>
    <div className="flex-1">
      <p className="font-black text-lg leading-tight">صور المواد</p>
      <p className="text-[11px] opacity-85">اختر المواد — تطلع صورة واسم فقط بدون أسعار</p>
    </div>
    <span className="font-black text-2xl" data-testid="photos-count">{count}</span>
  </div>
);

const Basket = ({ chosen, onRemove, onClear, busy, run }) => (
  <div className="bg-white rounded-3xl border-2 border-violet-100 p-3 space-y-3" data-testid="photos-basket">
    <div className="flex items-center justify-between">
      <p className="font-black text-sm text-violet-900">السلة ({chosen.length})</p>
      {chosen.length > 0 && (
        <button type="button" onClick={onClear} data-testid="photos-clear" className="text-[11px] font-bold text-red-600 flex items-center gap-1">
          <Trash2 className="w-3.5 h-3.5" /> مسح الكل
        </button>
      )}
    </div>
    {chosen.length === 0 ? (
      <p className="text-xs text-slate-400 text-center py-3">اضغط على المواد تحت حتى تضيفها</p>
    ) : (
      <div className="flex gap-2 overflow-x-auto pb-1">
        {chosen.map((p) => (
          <div key={p.id} className="relative shrink-0 w-20" data-testid={`photos-basket-item-${p.id}`}>
            <img src={productImgUrl(p)} alt="" className="w-20 h-20 object-contain rounded-xl bg-slate-50 border" />
            <p className="text-[9px] font-bold line-clamp-2 mt-0.5">{p.name_ar}</p>
            <button type="button" onClick={() => onRemove(p.id)} data-testid={`photos-remove-${p.id}`}
              className="absolute -top-1.5 -left-1.5 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center shadow">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    )}
    <div className="grid grid-cols-3 gap-2">
      <ActionBtn id="print" icon={Printer} label="طباعة" color="#1E3A5F" busy={busy} run={run} />
      <ActionBtn id="pdf" icon={FileDown} label="تنزيل PDF" color="#7C3AED" busy={busy} run={run} />
      <ActionBtn id="share" icon={Share2} label="واتساب" color="#16A34A" busy={busy} run={run} />
    </div>
  </div>
);

const ActionBtn = ({ id, icon: Icon, label, color, busy, run }) => (
  <button type="button" onClick={() => run(id)} disabled={!!busy} data-testid={`photos-${id}-btn`}
    className="py-2.5 rounded-2xl text-white font-black text-xs flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-95 transition-transform"
    style={{ background: color }}>
    {busy === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {label}
  </button>
);

const PickCard = ({ p, on, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`photos-pick-${p.id}`}
    className={`relative bg-white rounded-2xl border-2 p-2 text-right transition-colors ${on ? "border-violet-600 bg-violet-50" : "border-slate-100 hover:border-violet-300"}`}>
    <img src={productImgUrl(p)} alt="" loading="lazy" className="w-full aspect-square object-contain rounded-xl bg-slate-50" />
    <p className="text-xs font-bold mt-1.5 line-clamp-2 min-h-[2rem]">{p.name_ar}</p>
    {on && (
      <span className="absolute top-2 left-2 w-7 h-7 rounded-full bg-violet-600 text-white flex items-center justify-center shadow">
        <Check className="w-4 h-4" strokeWidth={3} />
      </span>
    )}
  </button>
);

export default ProductPhotosTab;
